import React, { useState, useEffect, useRef } from 'react';
import saveAs from 'file-saver';
import { Button, MenuItem, Menu } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Alert } from '@mui/material';
import { round } from 'lodash';
import { ConsiderationType } from 'further-types/exit';
import { downloadShareConsiderationTemplate as downloadTemplate } from '../ReviewShareConsiderations/downloadShareConsiderationTemplate';
import type { ShareConsideration } from '../types';

const loadExcelJs = async () => (await import('exceljs')).default;

const useStyles = makeStyles()(() => ({
  menuItem: {
    letterSpacing: '0px',
    fontSize: '0.875rem',
  },
}));

const greyOutColumn = {
  fill: {
    type: 'pattern' as const,
    pattern: 'solid' as const,
    fgColor: { argb: 'e6e6e6' },
    bgColor: { argb: 'FF0000FF' },
  },
};
const defaultColumnStyle = {
  fill: {
    type: 'pattern',
    pattern: 'none',
  },
};

const format2dp = {
  numFmt: '£#,##0.00',
};

const noOfSharesFormat = {
  numFmt: '#,##0',
};

const wide = {
  width: 20,
};

const extraWide = {
  width: 30,
};

const commonColumnsStart = [
  {
    header: 'System shareholding ID',
    key: 'shareholdingId',
    style: { ...greyOutColumn },
    ...extraWide,
  },
  {
    header: 'External subscription ID',
    key: 'externalInvestmentId',
    style: { ...greyOutColumn },
    ...wide,
  },
  {
    header: 'Investor name',
    key: 'investorName',
    style: { ...greyOutColumn },
    ...wide,
  },
  { header: 'Tranche', key: 'tranche', style: { ...greyOutColumn }, ...wide },
  {
    header: 'Count of shares',
    key: 'noOfShares',
    style: { ...greyOutColumn, ...noOfSharesFormat },
    ...wide,
  },
];

const commonColumnsEnd = [
  {
    header: 'Net return',
    key: 'totalToBeReturned',
    style: { ...greyOutColumn, ...format2dp },
  },
  {
    header: 'Taxable value share price',
    key: 'taxableValueSharePrice',
    style: { ...format2dp },
    ...wide,
  },
  {
    header: 'Net taxable value',
    key: 'taxableValue',
    style: { ...greyOutColumn, ...format2dp },
    ...wide,
  },
];

const createExitcolumns = [
  ...commonColumnsStart,
  {
    header: 'Shares allocated for sale',
    key: 'sharesAllocatedForSale',
    style: { ...defaultColumnStyle },
    ...wide,
  },
  {
    header: 'Total Received value',
    key: 'totalSaleValue',
    style: { ...format2dp },
    ...wide,
  },
  {
    header: 'Performance fee',
    key: 'performanceFee',
    style: { ...format2dp },
  },
  {
    header: 'Accrued fees charged',
    key: 'accruedFeesCharged',
    style: { ...format2dp },
  },
  ...commonColumnsEnd,
];

const editedExitColumns = [
  ...commonColumnsStart,
  {
    header: 'Previous accrued fees charged',
    key: 'previousAccruedFeesCharged',
    style: { ...greyOutColumn, ...format2dp },
  },
  {
    header: 'Previous performance fees charged',
    key: 'previousPerformanceFeesCharged',
    style: { ...greyOutColumn, ...format2dp },
  },
  {
    header: 'Previously received value',
    key: 'previousGrossReceivedValue',
    style: { ...greyOutColumn, ...format2dp },
  },
  {
    header: 'Shares allocated for sale',
    key: 'sharesAllocatedForSale',
    style: { ...greyOutColumn },
  },
  {
    header: 'Total Received value',
    key: 'totalSaleValue',
    style: { ...format2dp, ...greyOutColumn },
    ...wide,
  },
  {
    header: 'Additional received value',
    key: 'additionalGrossReceivedValue',
    style: { ...format2dp, ...defaultColumnStyle },
    ...wide,
  },
  {
    header: 'Additional performance fee',
    key: 'performanceFee',
    style: { ...format2dp },
    ...wide,
  },
  {
    header: 'Additional accrued fees',
    key: 'accruedFeesCharged',
    style: { ...format2dp },
    ...wide,
  },
  ...commonColumnsEnd,
];

type Props = {
  shareholdings?: Array<any>;
  setShareholdings?: React.Dispatch<React.SetStateAction<Array<any>>>;
  shareConsiderations?: Array<ShareConsideration>;
  setShareConsiderations?: React.Dispatch<
    React.SetStateAction<Array<ShareConsideration>>
  >;
  exitDetails?: any;
  disableTable: boolean;
  isBeingEdited: boolean;
};

const AmendmentsUpload: React.FC<Props> = ({
  shareholdings = [],
  setShareholdings,
  shareConsiderations = [],
  setShareConsiderations,
  exitDetails,
  disableTable,
  isBeingEdited,
}) => {
  const [amendmentsMenuAnchorEl, setAmendmentsMenuAnchorEl] = useState(null);
  const [amendmentsFile, setAmendmentsFile] = useState(null);
  const inputFileRef = useRef(null);
  const { classes } = useStyles();
  const columns = isBeingEdited ? editedExitColumns : createExitcolumns;
  const isShareConsideration =
    exitDetails?.considerationType === ConsiderationType.Share;

  const handleMenuOpen = (event) => {
    setAmendmentsMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAmendmentsMenuAnchorEl(null);
  };

  const downloadShareConsiderationTemplate = () => {
    downloadTemplate({
      exitDetails,
      shareConsiderations,
      setAllSharesToZero: false,
      filename: `Share-Exit-amendments-file-${Date.now()}.xlsx`,
      isBeingEdited,
    });
  };

  const downloadAmendmentsTemplate = async () => {
    if (isShareConsideration) {
      await downloadShareConsiderationTemplate();
      return;
    }

    const ExcelJS = await loadExcelJs();
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(`Exit-${Date.now()}`);

    worksheet.mergeCells('A1', 'P2');

    // Column Headers
    worksheet.getRow(3).values = columns.map((col) => col.header);

    //@ts-expect-error
    worksheet.columns = columns;

    // Instructions - applied 'after' to overwrite the duplicate column headers above
    worksheet.getCell('A1').value =
      'To use this file, please edit the fields in the white columns and reupload this file. If formulas are used, please remove them before reuploading.';
    worksheet.getRow(1).getCell(1).font = {
      bold: true,
      italic: true,
      color: { argb: '000' },
      size: 11,
    };
    worksheet.getRow(1).getCell(1).alignment = {
      vertical: 'middle',
    };

    // Data
    shareholdings.forEach((sh, i) => {
      const totalToBeReturnedFormula = isBeingEdited
        ? `ROUND(J${i + 4}-L${i + 4}-M${i + 4}-F${i + 4}-G${i + 4}, 2)`
        : `ROUND(G${i + 4}-H${i + 4}-I${i + 4}, 2)`;

      const taxableValueFormula = isBeingEdited
        ? `ROUND((I${i + 4}*O${i + 4})-L${i + 4}-M${i + 4}-F${i + 4}-G${
            i + 4
          }, 2)`
        : `ROUND((F${i + 4}*K${i + 4})-H${i + 4}-I${i + 4}, 2)`;

      worksheet.addRows([
        {
          shareholdingId: sh.shareholdingId,
          externalInvestmentId: sh.externalInvestmentId,
          investorName: sh.investorName,
          tranche: sh.tranche?.fundName,
          noOfShares: sh.noOfShares,
          previousAccruedFeesCharged: sh.previousAccruedFeesCharged,
          previousPerformanceFeesCharged: sh.previousPerformanceFeesCharged,
          previousGrossReceivedValue: sh.previousGrossReceivedValue,
          sharesAllocatedForSale: sh.sharesAllocatedForSale,
          totalSaleValue: isBeingEdited
            ? { formula: `ROUND(H${i + 4}+K${i + 4}, 2)` }
            : sh.totalSaleValue,
          additionalGrossReceivedValue:
            (Number(sh.totalSaleValue) || 0) -
            (Number(sh.previousGrossReceivedValue) || 0),
          performanceFee: sh.performanceFee,
          accruedFeesCharged: sh.accruedFeesCharged,
          totalToBeReturned: {
            formula: totalToBeReturnedFormula,
          },
          taxableValueSharePrice: sh.taxableValueSharePrice,
          taxableValue: {
            formula: taxableValueFormula,
          },
        },
      ]);
    });

    workbook.xlsx.writeBuffer().then(function (buffer) {
      saveAs(
        new Blob([buffer], { type: 'application/octet-stream' }),
        `Exits-amendments-file-${Date.now()}.xlsx`,
      );
    });
  };

  const onFileInputChange = (e) => {
    setAmendmentsFile(e.target.files[0]);
    // reset the input so if the same file is upload again, this onChange is triggered
    //@ts-expect-error
    inputFileRef.current.value = '';
  };

  useEffect(() => {
    if (!amendmentsFile) return;

    const currentIsShareConsideration =
      exitDetails?.considerationType === ConsiderationType.Share;
    const currentShareConsiderations = shareConsiderations;
    const currentShareholdings = shareholdings;
    const currentSetShareConsiderations = setShareConsiderations;
    const currentSetShareholdings = setShareholdings;
    const currentColumns = isBeingEdited
      ? editedExitColumns
      : createExitcolumns;
    const currentIsBeingEdited = isBeingEdited;

    const wbPromise = loadExcelJs();
    const reader = new FileReader();
    const updateValues = new Map();

    reader.readAsArrayBuffer(amendmentsFile);
    reader.onload = () => {
      const buffer = reader.result;
      wbPromise.then((ExcelJS) => {
        const wb = new ExcelJS.Workbook();
        //@ts-expect-error
        wb.xlsx.load(buffer).then((workbook) => {
          workbook.eachSheet((sheet) => {
            sheet.eachRow((row, rowIndex) => {
              if (currentIsShareConsideration) {
                // For share consideration, ignore first 9 rows (instructions + headers)
                if (rowIndex <= 7) return;

                // Column M = Shareholding ID (13th column, index 13) - this is the actual shareholdingId
                // Column G = Exit shares sold (7th column, index 7)
                // Column H = New shares allocated (8th column, index 8)
                const shareholdingIdCell = row.getCell(13);
                const exitSharesSoldCell = row.getCell(7);
                const newSharesAllocatedCell = row.getCell(8);

                const shareholdingIdValue = shareholdingIdCell.value;
                const exitSharesSoldValue = exitSharesSoldCell.value;
                const newSharesAllocatedValue = newSharesAllocatedCell.value;

                // Skip empty rows
                if (!shareholdingIdValue) return;

                // Extract the actual value (handle formula results)
                const shareholdingId = String(
                  //@ts-expect-error
                  shareholdingIdValue?.result || shareholdingIdValue || '',
                ).trim();
                const exitSharesSoldRaw =
                  //@ts-expect-error
                  exitSharesSoldValue?.result || exitSharesSoldValue;
                const newSharesAllocatedRaw =
                  //@ts-expect-error
                  newSharesAllocatedValue?.result || newSharesAllocatedValue;

                // Convert to number and round to 10 decimal places
                const exitSharesSold = round(
                  Number(exitSharesSoldRaw) || 0,
                  10,
                );
                const newSharesAllocated = round(
                  Number(newSharesAllocatedRaw) || 0,
                  10,
                );

                updateValues.set(shareholdingId, {
                  exitSharesSold,
                  newSharesAllocated,
                });
                return;
              }

              // Cash consideration logic (existing)
              // Ignore Introduction and column headers
              if (rowIndex <= 3) return;

              const shareholdingIdI = currentColumns.findIndex(
                (col) => col.key === 'shareholdingId',
              );
              const sharesAllocatedForSaleI = currentColumns.findIndex(
                (col) => col.key === 'sharesAllocatedForSale',
              );
              const totalSaleValueI = currentColumns.findIndex(
                (col) => col.key === 'totalSaleValue',
              );
              const performanceFeeI = currentColumns.findIndex(
                (col) => col.key === 'performanceFee',
              );
              const accruedFeesI = currentColumns.findIndex(
                (col) => col.key === 'accruedFeesCharged',
              );
              const taxableValueSharePriceI = currentColumns.findIndex(
                (col) => col.key === 'taxableValueSharePrice',
              );

              const shareholdingId = row.getCell(shareholdingIdI + 1).value;
              const sharesAllocatedForSale = row.getCell(
                sharesAllocatedForSaleI + 1,
              ).value;
              const totalSaleValue = row.getCell(totalSaleValueI + 1).value;
              const performanceFee = row.getCell(performanceFeeI + 1).value;
              const accruedFees = row.getCell(accruedFeesI + 1).value;
              const taxableValueSharePrice = row.getCell(
                taxableValueSharePriceI + 1,
              ).value;

              const updateObject = {
                totalSaleValue: round(
                  //@ts-expect-error
                  Number(totalSaleValue?.result || totalSaleValue) || 0,
                  2,
                ),
                performanceFee: round(
                  //@ts-expect-error
                  Number(performanceFee?.result || performanceFee) || 0,
                  2,
                ),
                accruedFeesCharged: round(
                  //@ts-expect-error
                  Number(accruedFees?.result || accruedFees) || 0,
                  2,
                ),
                taxableValueSharePrice:
                  Number(
                    //@ts-expect-error
                    taxableValueSharePrice?.result || taxableValueSharePrice,
                  ) || 0,
              };
              if (!currentIsBeingEdited) {
                //@ts-expect-error
                updateObject.sharesAllocatedForSale =
                  Number(
                    //@ts-expect-error
                    sharesAllocatedForSale?.result || sharesAllocatedForSale,
                  ) || 0;
              }
              updateValues.set(
                //@ts-expect-error
                shareholdingId.result || shareholdingId,
                updateObject,
              );
            });
          });

          if (updateValues.size) {
            if (currentIsShareConsideration && currentSetShareConsiderations) {
              // Update both sharesAllocatedForSale and newSharesAllocated from the file
              const updatedShareConsiderations = currentShareConsiderations.map(
                (sc) => {
                  const shareholdingIdKey = String(sc.shareholdingId).trim();
                  const updates = updateValues.get(shareholdingIdKey);
                  if (updates !== undefined) {
                    return {
                      ...sc,
                      sharesAllocatedForSale: updates.exitSharesSold,
                      newSharesAllocated: updates.newSharesAllocated,
                    };
                  }
                  return sc;
                },
              );
              currentSetShareConsiderations(updatedShareConsiderations);
              setAmendmentsFile(null);
            } else if (currentSetShareholdings) {
              const newShareholdings = currentShareholdings.map((sh) => {
                if (updateValues.has(sh.shareholdingId)) {
                  const updates = updateValues.get(sh.shareholdingId);
                  const totalSaleValue = Number(updates.totalSaleValue) || 0;
                  const accruedFees = Number(updates.accruedFeesCharged) || 0;
                  const perfFee = Number(updates.performanceFee) || 0;
                  let totalToBeReturned =
                    totalSaleValue - accruedFees - perfFee;
                  const shares =
                    Number(
                      currentIsBeingEdited
                        ? sh.sharesAllocatedForSale
                        : updates.sharesAllocatedForSale,
                    ) || 0;
                  const taxPrice = Number(updates.taxableValueSharePrice) || 0;
                  const grossTaxableValue = shares * taxPrice;

                  let additionalGrossReceivedValue = 0;
                  if (currentIsBeingEdited) {
                    const prevAccrued =
                      Number(sh.previousAccruedFeesCharged) || 0;
                    const prevPerf =
                      Number(sh.previousPerformanceFeesCharged) || 0;
                    totalToBeReturned =
                      totalToBeReturned - prevAccrued - prevPerf;
                    const previousGrossReceivedValue =
                      Number(sh.previousGrossReceivedValue) || 0;
                    additionalGrossReceivedValue =
                      totalSaleValue - previousGrossReceivedValue;
                  }

                  return {
                    ...sh,
                    ...updates,
                    totalToBeReturned: Math.abs(round(totalToBeReturned, 2)),
                    grossTaxableValue: Math.abs(round(grossTaxableValue, 2)),
                    netTaxableValue: Math.abs(round(grossTaxableValue, 2)),
                    additionalGrossReceivedValue: round(
                      additionalGrossReceivedValue || 0,
                      2,
                    ),
                    // Mark this row as coming from an upload so PerformanceFeeCell doesn't recalculate
                    _fromUpload: true,
                  };
                } else {
                  return sh;
                }
              });

              currentSetShareholdings(newShareholdings);
              setAmendmentsFile(null);
            }
          }
        });
      });
    };
  }, [amendmentsFile]);

  const onInputClick = () => {
    setAmendmentsFile(null);
    //@ts-expect-error
    inputFileRef.current?.click();
  };

  return (
    <>
      <Button
        aria-controls="amendments-menu"
        aria-haspopup="true"
        onClick={handleMenuOpen}
        variant="outlined"
        color="primary"
      >
        Amendments
      </Button>
      <Menu
        id="amendments-menu"
        anchorEl={amendmentsMenuAnchorEl}
        keepMounted
        open={Boolean(amendmentsMenuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem className={classes.menuItem} onClick={onInputClick}>
          <input
            style={{ display: 'none' }}
            ref={inputFileRef}
            type="file"
            onChange={onFileInputChange}
          />
          Upload amendments file
        </MenuItem>
        <MenuItem
          className={classes.menuItem}
          onClick={downloadAmendmentsTemplate}
        >
          Download amendments template
        </MenuItem>
      </Menu>
      {disableTable && (
        <Alert severity="info">
          To make further amendments, please upload a new file
        </Alert>
      )}
    </>
  );
};

export default AmendmentsUpload;
